import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, Check, MessageCircle, Send, Package, Info, AlertCircle } from 'lucide-react'
import { QuotationBreakdown } from '@/types'
import { trpc } from '@/lib/trpc/client'

export default function ReviewProposalPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [quotation, setQuotation] = useState<QuotationBreakdown | null>(null)
  const [projectSpecs, setProjectSpecs] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [productImages, setProductImages] = useState<Record<string, { image: string; thumbnail: string }>>({})

  // Chat state
  const [chatMessages, setChatMessages] = useState<Array<{role: 'user' | 'assistant', content: string}>>([])
  const [chatInput, setChatInput] = useState('')
  const [isChatLoading, setIsChatLoading] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)

  const consultationMutation = trpc.consultation.chat.useMutation()
  const quotationMutation = trpc.quotation.generate.useMutation()

  useEffect(() => {
    const messagesParam = searchParams.get('messages')
    if (!messagesParam) {
      navigate('/')
      return
    }

    const messages = JSON.parse(decodeURIComponent(messagesParam))
    generateQuotation(messages)

    setChatMessages(messages.concat([
      {
        role: 'assistant',
        content: 'I can help you adjust project details or answer questions about the system. Just ask!'
      }
    ]))

    fetch('/data/product-images.json')
      .then(res => res.json())
      .then(data => setProductImages(data))
      .catch(err => console.error('Failed to load product images:', err))
  }, [searchParams, navigate])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages])

  const generateQuotation = async (messages: any[]) => {
    try {
      const result = await quotationMutation.mutateAsync({ messages })

      if (result.success && result.quotation) {
        setQuotation(result.quotation as any)
        setProjectSpecs({ messages })
      }
    } catch (error) {
      console.error('Error generating quotation:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return

    const userMessage = chatInput.trim()
    setChatInput('')
    setIsChatLoading(true)

    const updatedChatMessages = [...chatMessages, { role: 'user' as const, content: userMessage }]
    setChatMessages(updatedChatMessages)

    try {
      const lowerMessage = userMessage.toLowerCase()
      const actionKeywords = ['change', 'update', 'set', 'modify', 'adjust', 'make it', 'switch to', 'replace', 'convert to', 'make']
      const hasAction = actionKeywords.some(keyword => lowerMessage.includes(keyword))

      const targetKeywords = ['area', 'surface', 'sqm', 'm²', 'square meter', 'size', 'location', 'exterior', 'interior',
        'outdoor', 'indoor', 'outside', 'inside', 'concrete', 'tiles', 'gypsum', 'ceramic', 'cement', 'screed',
        'bathroom', 'terrace', 'pool', 'foundation', 'basement', 'traffic', 'heavy', 'light', 'medium',
        'moisture', 'humidity', 'wet', 'dry']
      const hasTarget = targetKeywords.some(keyword => lowerMessage.includes(keyword))

      const questionWords = ['what', 'why', 'how', 'when', 'where', 'which', 'who', '?', 'tell me', 'explain']
      const isQuestion = questionWords.some(word => lowerMessage.includes(word))

      const isUpdateRequest = (hasAction || (hasTarget && !isQuestion))

      if (isUpdateRequest && projectSpecs) {
        const updatedMessages = [
          ...projectSpecs.messages,
          { role: 'user', content: userMessage }
        ]

        const result = await quotationMutation.mutateAsync({ messages: updatedMessages })

        if (result.success && result.quotation) {
          setProjectSpecs({ messages: updatedMessages })
          setQuotation(result.quotation as any)

          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: 'Updated! The system has been recalculated based on your changes.'
          }])
        } else {
          throw new Error('Failed to update specification')
        }
      } else {
        const offerContext = { projectSpecs, quotation }

        const result = await consultationMutation.mutateAsync({
          messages: [
            {
              role: 'system',
              content: `You are a Sika technical consultant. Current project: ${JSON.stringify(offerContext)}. Help the client understand the system and products. Be concise and professional.`
            },
            ...updatedChatMessages
          ],
        })

        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: result.content
        }])
      }
    } catch (error) {
      console.error('Chat error:', error)
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, an error occurred. Please try again.'
      }])
    } finally {
      setIsChatLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#D32F2F] mx-auto mb-4" />
          <p className="text-xl text-slate-700 font-semibold">Generating Your Solution</p>
          <p className="text-sm text-slate-500 mt-2">Analyzing project requirements and selecting optimal products...</p>
        </div>
      </div>
    )
  }

  if (!quotation) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Could not generate system specification</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 mb-4">An error occurred. Please try again.</p>
            <Button onClick={() => navigate('/')} className="w-full">
              Back to consultation
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const calculateTotals = () => {
    let materials = 0
    quotation.phases.forEach(phase => {
      phase.products.forEach(product => {
        // Handle both number and Money object types
        const price = typeof product.totalPrice === 'number'
          ? product.totalPrice
          : (product.totalPrice as any)?.amount ?? 0
        materials += price
      })
    })

    const vat = materials * 0.19
    const total = materials + vat

    return {
      materials: Math.round(materials * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      total: Math.round(total * 100) / 100,
    }
  }

  const totals = calculateTotals()

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Professional Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <a href="/" className="inline-block">
                <img
                  src="/sika-logo.svg"
                  alt="Sika"
                  width={120}
                  height={40}
                  className="opacity-90 hover:opacity-100 transition-opacity"
                />
              </a>
              <div className="border-l border-slate-300 pl-6">
                <h1 className="text-2xl font-bold text-slate-900">Complete System Specification</h1>
                <p className="text-sm text-slate-500">Engineered Solution for Your Project</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-slate-500">Total System Investment</div>
              <div className="text-3xl font-bold text-[#D32F2F]">{totals.materials.toLocaleString()} EUR</div>
              <div className="text-xs text-slate-400">+ {totals.vat.toLocaleString()} EUR VAT (19%)</div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-[1fr_380px] gap-8">
          {/* LEFT: System Overview */}
          <div className="space-y-6">
            {/* Project Summary Card */}
            {projectSpecs && (
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-slate-900 mb-1">{projectSpecs.projectType}</h2>
                      <p className="text-slate-600 text-sm">Integrated system engineered for {projectSpecs.surfaceArea || projectSpecs.dimensions?.area} m² project area</p>
                    </div>
                    {quotation.projectSnapshot?.warranty && (
                      <Badge className="bg-green-50 text-green-700 border-green-200 px-3 py-1">
                        <Check className="w-3 h-3 mr-1" />
                        {quotation.projectSnapshot.warranty}
                      </Badge>
                    )}
                  </div>

                  {/* Key Project Details */}
                  <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                    {projectSpecs.surfaceType && (
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Substrate</div>
                        <div className="font-semibold text-slate-900">{projectSpecs.surfaceType}</div>
                      </div>
                    )}
                    {projectSpecs.environmentalFactors?.climate && (
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Location</div>
                        <div className="font-semibold text-slate-900 capitalize">{projectSpecs.environmentalFactors.climate}</div>
                      </div>
                    )}
                    {projectSpecs.environmentalFactors?.traffic && (
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Traffic Load</div>
                        <div className="font-semibold text-slate-900 capitalize">{projectSpecs.environmentalFactors.traffic}</div>
                      </div>
                    )}
                  </div>

                  {/* System Rationale */}
                  {quotation.systemExplanation && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-blue-900 text-sm mb-1">Why This System</div>
                          <p className="text-sm text-blue-900 mb-2">{quotation.systemExplanation.title}</p>
                          <ul className="text-xs text-blue-800 space-y-1">
                            {quotation.systemExplanation.benefits.map((benefit, idx) => (
                              <li key={idx}>• {benefit}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Assumptions */}
                  {projectSpecs.assumptions && projectSpecs.assumptions.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-amber-500" />
                        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Project Assumptions</span>
                      </div>
                      <div className="space-y-2">
                        {projectSpecs.assumptions.map((assumption: any, idx: number) => (
                          <div key={idx} className="text-xs bg-amber-50 p-2 rounded">
                            <span className="font-semibold text-amber-900">{assumption.category}:</span>{' '}
                            <span className="text-amber-800">{assumption.value}</span>
                            {assumption.reasoning && (
                              <div className="text-amber-700 italic mt-1">{assumption.reasoning}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* System Products - Flat List */}
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="border-b border-slate-100 bg-slate-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Complete System Components</CardTitle>
                  <div className="text-sm text-slate-600">
                    {quotation.phases.reduce((sum, p) => sum + p.products.length, 0)} products
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {quotation.phases.flatMap((phase, phaseIdx) =>
                  phase.products.map((product, prodIdx) => (
                    <div
                      key={`${phase.name}-${phaseIdx}-${product.productId}-${prodIdx}`}
                      className="px-6 py-4 border-b border-slate-100 last:border-b-0"
                    >
                      <div className="flex gap-4">
                        {/* Product Image */}
                        <div className="w-24 h-24 bg-slate-100 rounded-lg flex-shrink-0 overflow-hidden border border-slate-200">
                          {(() => {
                            // Use matnr for image lookup (product-images.json is indexed by MATNR)
                            const imageData = (product as any).matnr ? (productImages as any)[(product as any).matnr] : null;
                            const imageSrc = imageData?.thumbnail || imageData?.image;

                            return imageSrc ? (
                              <img
                                src={imageSrc}
                                alt={product.productName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-8 h-8 text-slate-300" />
                              </div>
                            );
                          })()}
                        </div>

                        {/* Product Details */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-bold text-slate-900">{product.productName}</h4>
                                {product.isRequired && (
                                  <Badge className="bg-green-100 text-green-700 text-xs px-2 py-0.5">
                                    Essential
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-600 mt-1">{product.reasoning}</p>
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-2xl font-bold text-slate-900">
                                {typeof product.totalPrice === 'number'
                                  ? Math.round(product.totalPrice)
                                  : Math.round(product.totalPrice.amount)} EUR
                              </div>
                              {product.packagesNeeded && product.packageSize ? (
                                <div className="text-xs text-slate-500">
                                  {typeof product.totalPrice === 'number'
                                    ? (product.totalPrice / product.packagesNeeded).toFixed(2)
                                    : (product.totalPrice.amount / product.packagesNeeded).toFixed(2)} EUR per {product.packagingType}
                                </div>
                              ) : (
                                <div className="text-xs text-slate-500">
                                  {typeof product.unitPrice === 'number'
                                    ? product.unitPrice.toFixed(2)
                                    : product.unitPrice.amount.toFixed(2)} EUR per {product.unit}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Technical Reasoning */}
                          {product.reasoning && (
                            <div className="mt-2 p-3 bg-blue-50 rounded text-sm text-blue-900">
                              <strong className="text-blue-700">Why this product:</strong> {product.reasoning}
                            </div>
                          )}

                          {/* Quantity & Packaging */}
                          <div className="mt-3 flex items-center gap-6 text-sm">
                            <div className="flex items-center gap-2">
                              <Package className="w-4 h-4 text-slate-400" />
                              {product.packagesNeeded && product.packageSize ? (
                                <span className="text-slate-700">
                                  <strong className="text-base">{product.packagesNeeded} {product.packagingType}{product.packagesNeeded > 1 ? 's' : ''}</strong>
                                  <span className="text-slate-500 ml-2">
                                    ({product.packageSize} {product.unit} each • Total: {product.quantity} {product.unit})
                                  </span>
                                </span>
                              ) : (
                                <span className="text-slate-700">
                                  <strong>{product.quantity} {product.unit}</strong>
                                </span>
                              )}
                            </div>
                            {product.usageRate && product.usageRate > 50 && (
                              <div className="text-xs text-green-600">
                                ✓ Used in {product.usageRate}% of similar projects
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Investment Summary */}
            <Card className="border-slate-200 bg-white shadow-sm border-2 border-[#D32F2F]">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg text-slate-900 mb-4">System Investment Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-slate-700">
                    <span>Complete System Materials</span>
                    <span className="font-semibold">{totals.materials.toLocaleString()} EUR</span>
                  </div>
                  <div className="flex justify-between text-slate-700">
                    <span>VAT (19%)</span>
                    <span className="font-semibold">{totals.vat.toLocaleString()} EUR</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between text-2xl font-bold text-slate-900">
                    <span>Total Project Investment</span>
                    <span className="text-[#D32F2F]">{totals.total.toLocaleString()} EUR</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-slate-50 rounded text-xs text-slate-600">
                  <p>This integrated system delivers durability, performance, and predictability across all project phases.</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT: Consultation Chat */}
          <div className="lg:sticky lg:top-8 h-fit">
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#D32F2F]" />
                  <CardTitle className="text-lg">Technical Consultation</CardTitle>
                </div>
                <p className="text-xs text-slate-500 mt-1">Adjust project details or ask about products</p>
              </CardHeader>
              <CardContent className="p-4">
                {/* Chat Messages */}
                <div className="h-[500px] overflow-y-auto space-y-3 mb-4 pr-2">
                  {chatMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] px-4 py-3 rounded-lg ${
                          msg.role === 'user'
                            ? 'bg-[#D32F2F] text-white'
                            : 'bg-slate-100 text-slate-900 border border-slate-200'
                        }`}
                      >
                        <p className="text-sm leading-relaxed">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  {isChatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 px-4 py-3 rounded-lg border border-slate-200">
                        <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Chat Input */}
                <div className="flex gap-2 pt-4 border-t border-slate-100">
                  <Textarea
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        sendChatMessage()
                      }
                    }}
                    placeholder="e.g., 'Change area to 50sqm' or 'Why is SikaTop Seal needed?'"
                    className="flex-1 min-h-[60px] max-h-[120px] resize-none text-sm"
                    disabled={isChatLoading}
                  />
                  <Button
                    onClick={sendChatMessage}
                    disabled={!chatInput.trim() || isChatLoading}
                    className="bg-[#D32F2F] hover:bg-[#b71c1c] h-[60px] w-[60px] p-0 flex-shrink-0"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  )
}
